import SectionMessage from '@atlaskit/section-message';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import t from 'modules/i18n/intl';
import { fetchChildrenOverviews } from '../duck/actions/accountId';
import { selectChildrenOverviews } from '../duck/selectors';
import {
  ATLASSIAN_ACCESS,
  ATLASSIAN_TOGETHER,
  ENTERPRISE_CONFLUENCE,
  ENTERPRISE_JSD,
  ENTERPRISE_JSW,
} from 'data/products/keys';

const CollapsingList = styled.ul<{ isCollapsed: boolean }>`
  display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'block')};
`;

const EnterpriseSubscriptionsBanner = ({
  atlassianOrgAccountId,
}: {
  atlassianOrgAccountId: number | null;
}) => {
  const dispatch = useDispatch();

  const [orgAccountIds, setOrgAccountIds] = useState<number[]>([]);
  const [hasEnterprise, setHasEnterprise] = useState(false);
  const [hasAT, setHasAT] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const orgChildren = useSelector(selectChildrenOverviews) as any[];

  useEffect(() => {
    if (atlassianOrgAccountId) {
      dispatch(fetchChildrenOverviews(atlassianOrgAccountId));
    }
  }, [atlassianOrgAccountId]);

  useEffect(() => {
    if (orgChildren?.length) {
      const accountIdsList: number[] = [];
      orgChildren.forEach(({ productKey, accountId }) => {
        switch (productKey) {
          case ATLASSIAN_ACCESS: {
            accountIdsList.push(atlassianOrgAccountId as number);
            break;
          }
          case ENTERPRISE_JSW:
          case ENTERPRISE_JSD:
          case ENTERPRISE_CONFLUENCE: {
            accountIdsList.push(accountId);
            setHasEnterprise(true);
            break;
          }
          case ATLASSIAN_TOGETHER: {
            accountIdsList.push(accountId);
            setHasAT(true);
            break;
          }
          default: {
            break;
          }
        }
      });
      setOrgAccountIds(accountIdsList);
    }
  }, [orgChildren?.length]);

  const handleCollapseClick = () => {
    setIsCollapsed((prevState) => !prevState);
  };

  return atlassianOrgAccountId && (hasEnterprise || hasAT) ? (
    <SectionMessage title={t('mac.licenses.enterprise.subscriptions.banner.title')}>
      <>
        <p>
          <FormattedMessage
            id="mac.licenses.enterprise.subscriptions.banner.p1"
            defaultMessage="Enterprise subscriptions are listed under your Atlassian Access SENs and share the same billing and technical contacts. Select Show/Hide to expand or collapse the list of your Atlassian Access SENs associated with Enterprise Subscriptions."
          />
        </p>
        <p onClick={handleCollapseClick} role="presentation">
          {isCollapsed ? (
            <ChevronRightLargeIcon label="" size="small" />
          ) : (
            <ChevronDownIcon label="" size="small" />
          )}
          {hasEnterprise && !hasAT && (
            <FormattedMessage
              id="mac.licenses.enterprise.subscriptions.banner.enterprise.only"
              defaultMessage="SENs with only Enterprise subscriptions"
            />
          )}
          {!hasEnterprise && hasAT && (
            <FormattedMessage
              id="mac.licenses.enterprise.subscriptions.banner.at.only"
              defaultMessage="SENs with only Atlassian Together subscriptions"
            />
          )}
          {hasEnterprise && hasAT && (
            <FormattedMessage
              id="mac.licenses.enterprise.subscriptions.banner.both"
              defaultMessage="SENs with both, Enterprise and Atlassian Together subscriptions"
            />
          )}
        </p>

        <CollapsingList isCollapsed={isCollapsed} data-cy="enterprise-banner-list">
          {orgAccountIds.map((id) => (
            <li key={`sen-${id}`}>SEN-{id}</li>
          ))}
        </CollapsingList>
      </>
    </SectionMessage>
  ) : null;
};

export default EnterpriseSubscriptionsBanner;
